import ArrowIcon from "@mui/icons-material/ArrowForward";
import FirmIcon from "@mui/icons-material/Business";
import SettlementsIcon from "@mui/icons-material/Handshake";
import ClientsIcon from "@mui/icons-material/People";
import { Box, Paper, Skeleton, SvgIconProps, Typography } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import { ComponentType, ReactNode, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Await, Link } from "react-router";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { usePersona } from "../../context/usePersona";
import { percentageNoDecimals } from "../../shared/percentage";
import { PortfolioOverview } from "../../shared/PortfolioTypes";
import {
  BAR_FILL_ALPHA,
  gainFill,
  NO_VALUE,
  signedTone,
  TABULAR_NUMERALS,
  toneColors,
} from "../general/dataAtoms/shared";
import { SectionHeading } from "../portfolio/PortfolioSection";

const FIRM_PATH = "/portfolio/firm";
const CLIENTS_PATH = "/portfolio/clients";
const SETTLEMENTS_PATH = "/portfolio/settlements";

/** How far the bars of the shares are lightened from the primary colour. */
const BAR_LIGHTEN = 0.35;

/** How high the bars of the win chances may grow, in px. */
const WIN_CHANCES_HEIGHT = 40;

const plainLink = { textDecoration: "none", color: "inherit" } as const;

/** What the cards have of the overview: the figures, or why they have none. */
type Figures =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "loaded"; overview: PortfolioOverview };

const percent = (share: number) => percentageNoDecimals.format(share);

/**
 * A whole as a bar in a rounded track, its parts side by side and square at
 * their ends, as the bars of the counts on the start page.
 */
const ShareBar = ({
  parts,
}: {
  parts: readonly { share: number; color: string }[];
}) => (
  <Box
    aria-hidden
    sx={{
      display: "flex",
      height: 10,
      mt: 0.75,
      borderRadius: 999,
      bgcolor: "action.hover",
      overflow: "hidden",
    }}
  >
    {parts.map(({ share, color }, index) => (
      <Box key={index} sx={{ width: `${share * 100}%`, bgcolor: color }} />
    ))}
  </Box>
);

/** A figure, or a few that belong together, leading to its section. */
const FigureLink = ({ to, children }: { to: string; children: ReactNode }) => (
  <Box
    component={Link}
    to={to}
    sx={{
      display: "block",
      mx: -1,
      px: 1,
      py: 0.75,
      borderRadius: 1.5,
      ...plainLink,
      "&:hover": { bgcolor: "action.hover" },
    }}
  >
    {children}
  </Box>
);

/** The figure a card leads with, and what it is of. */
const Headline = ({
  value,
  suffix,
  caption,
}: {
  value: string;
  suffix?: string;
  caption: string;
}) => (
  <>
    <Typography
      variant="h4"
      component="p"
      sx={{
        fontWeight: 700,
        letterSpacing: "-.02em",
        lineHeight: 1.2,
        ...TABULAR_NUMERALS,
      }}
    >
      {value}
      {/* a space of its own, so the figure and what follows read apart */}
      {suffix && " "}
      {suffix && (
        <Box
          component="span"
          sx={{
            ml: 0.25,
            typography: "subtitle1",
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          {suffix}
        </Box>
      )}
    </Typography>
    <Typography
      variant="caption"
      sx={{ display: "block", color: "text.secondary" }}
    >
      {caption}
    </Typography>
  </>
);

/** A figure on a line of its own: what it is, and the figure. */
const Row = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 1,
    }}
  >
    <Typography variant="body2" component="span">
      {label}
    </Typography>
    <Typography
      variant="body2"
      component="span"
      sx={{ fontWeight: 700, color, ...TABULAR_NUMERALS }}
    >
      {value}
    </Typography>
  </Box>
);

/** Two ends of a bar, or its start, middle and end, named under it. */
const BarEnds = ({ names }: { names: readonly string[] }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 1,
      mt: 0.5,
      typography: "caption",
      color: "text.secondary",
    }}
  >
    {names.map((name) => (
      <span key={name}>{name}</span>
    ))}
  </Box>
);

/** A card of the portfolio: its page in the head, its figures below. */
const OverviewCard = ({
  Icon,
  label,
  to,
  figures,
  children,
}: {
  Icon: ComponentType<SvgIconProps>;
  label: string;
  to: string;
  figures: Figures;
  children: (overview: PortfolioOverview) => ReactNode;
}) => (
  <Paper
    component="section"
    variant="outlined"
    sx={{
      borderRadius: 3,
      px: 2.5,
      pt: 2,
      pb: 2.25,
      display: "flex",
      flexDirection: "column",
      gap: 1,
    }}
  >
    <Typography component="h3" sx={{ fontWeight: 700 }}>
      <Box
        component={Link}
        to={to}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          ...plainLink,
        }}
      >
        <Box
          component="span"
          sx={{ display: "flex", alignItems: "center", gap: 1.125 }}
        >
          <Icon fontSize="small" sx={{ color: "primary.main" }} />
          {label}
        </Box>
        <ArrowIcon sx={{ fontSize: 16, color: "text.disabled" }} />
      </Box>
    </Typography>
    {figures.kind === "loaded" && children(figures.overview)}
    {figures.kind === "loading" && (
      <Box aria-hidden>
        <Skeleton variant="text" sx={{ typography: "h4", width: "40%" }} />
        <Skeleton variant="text" sx={{ width: "85%" }} />
        <Skeleton variant="text" sx={{ width: "70%" }} />
      </Box>
    )}
  </Paper>
);

/** How many clients have a win chance in each tenth, as bars over 0–100%. */
const WinChances = ({ bins }: { bins: readonly number[] }) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "home.portfolio" });
  const most = Math.max(0, ...bins);
  const range = (index: number) =>
    `${percent(index / bins.length)}–${percent((index + 1) / bins.length)}`;
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: "3px",
          height: WIN_CHANCES_HEIGHT,
          mt: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {bins.map((count, index) => (
          <Box
            key={index}
            title={t("clients.winChanceBin", { count, range: range(index) })}
            sx={{
              flex: "1 1 0",
              height: most > 0 ? `${(count / most) * 100}%` : 0,
              bgcolor: lighten(theme.palette.primary.main, BAR_LIGHTEN),
              borderRadius: "2px 2px 0 0",
            }}
          />
        ))}
      </Box>
      <BarEnds names={[percent(0), percent(0.5), percent(1)]} />
    </>
  );
};

/**
 * The portfolio on the start page: the cases taken together, apart from the
 * work on them above, in a card for each page of the portfolio. Every figure
 * leads to the section of its page that holds it. Totals and shares only: no
 * amount of a single case, and no profit of the firm, as this is the page
 * others see over a shoulder. The figures come after the cases, so the cards
 * wait for them, and say so where they could not be loaded.
 */
export const PortfolioArea = ({
  overview,
  total,
}: {
  overview: Promise<PortfolioOverview> | PortfolioOverview;
  /** How many cases there are. */
  total: number;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "home" });
  const cards = (figures: Figures) => (
    <OverviewCards figures={figures} total={total} />
  );

  return (
    <Box
      component="section"
      aria-labelledby="portfolio"
      sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}
    >
      <div>
        <SectionHeading id="portfolio" rule>
          {t("section.portfolio.title")}
        </SectionHeading>
        <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
          {t("portfolio.description", { count: total })}
        </Typography>
      </div>
      <Suspense fallback={cards({ kind: "loading" })}>
        <Await resolve={overview} errorElement={cards({ kind: "failed" })}>
          {(resolved: PortfolioOverview) =>
            cards({ kind: "loaded", overview: resolved })
          }
        </Await>
      </Suspense>
    </Box>
  );
};

const OverviewCards = ({
  figures,
  total,
}: {
  figures: Figures;
  total: number;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { active: persona } = usePersona();
  const { t } = useTranslation(undefined, { keyPrefix: "home.portfolio" });
  const tones = toneColors(theme);
  const barColor = lighten(theme.palette.primary.main, BAR_LIGHTEN);

  return (
    <>
      {figures.kind === "failed" && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("failed")}
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 2.25,
        }}
      >
        {/* the firm: how far the work and the revenue have come, in shares;
            the law firm's alone */}
        {persona === "lawFirm" && (
          <OverviewCard
            Icon={FirmIcon}
            label={t("firm.label")}
            to={FIRM_PATH}
            figures={figures}
          >
            {({ firm: { costProgress, revenueEarned } }) => (
              <>
                <FigureLink to={`${FIRM_PATH}#revenue`}>
                  <Headline
                    value={
                      costProgress ? percent(costProgress.incurred) : NO_VALUE
                    }
                    caption={
                      costProgress
                        ? t("firm.costs", { count: costProgress.activeCases })
                        : t("firm.noActiveCases")
                    }
                  />
                  {costProgress && (
                    <ShareBar
                      parts={[
                        { share: costProgress.incurred, color: barColor },
                      ]}
                    />
                  )}
                </FigureLink>
                <FigureLink to={`${FIRM_PATH}#revenue`}>
                  <Row
                    label={t("firm.revenueEarned")}
                    value={
                      revenueEarned === undefined
                        ? NO_VALUE
                        : percent(revenueEarned)
                    }
                  />
                  {revenueEarned !== undefined && (
                    <>
                      <ShareBar
                        parts={[{ share: revenueEarned, color: barColor }]}
                      />
                      <BarEnds names={[t("firm.earned"), t("firm.toCome")]} />
                    </>
                  )}
                </FigureLink>
              </>
            )}
          </OverviewCard>
        )}

        {/* the clients: what their cases are worth to them, how likely they
            are to win, what is claimed, and the chance of a loss in all */}
        <OverviewCard
          Icon={ClientsIcon}
          label={t("clients.label")}
          to={CLIENTS_PATH}
          figures={figures}
        >
          {({
            clients: {
              open,
              claimedByClients,
              claimedAgainstClients,
              lossChance,
            },
          }) => {
            const claimed = claimedByClients + claimedAgainstClients;
            const againstColor = alpha(
              theme.palette.data.negative.main,
              BAR_FILL_ALPHA,
            );
            const legend = (color: string, label: string, amount: number) => (
              <Box
                component="span"
                sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
              >
                <Box
                  component="span"
                  sx={{
                    flex: "none",
                    width: 9,
                    height: 9,
                    borderRadius: 0.5,
                    bgcolor: color,
                  }}
                />
                {label}
                <Box
                  component="span"
                  sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}
                >
                  {currency.formatCompact(amount)}
                </Box>
              </Box>
            );
            return (
              <>
                {open ? (
                  <>
                    <FigureLink to={`${CLIENTS_PATH}#caseValues`}>
                      <Headline
                        value={currency.formatCompact(open.expectedValue)}
                        caption={t("clients.expectedValue", {
                          count: open.cases,
                        })}
                      />
                    </FigureLink>
                    <FigureLink to={`${CLIENTS_PATH}#winChances`}>
                      <Row
                        label={t("clients.winChance")}
                        value={percent(open.winChance)}
                      />
                      <WinChances bins={open.winChanceBins} />
                    </FigureLink>
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ py: 0.75, color: "text.secondary" }}
                  >
                    {t("clients.allSettled")}
                  </Typography>
                )}
                <FigureLink to={`${CLIENTS_PATH}#quantums`}>
                  <Typography variant="body2">
                    {t("clients.claimed")}
                  </Typography>
                  <ShareBar
                    parts={
                      claimed > 0
                        ? [
                            {
                              share: claimedByClients / claimed,
                              color: gainFill(theme),
                            },
                            {
                              share: claimedAgainstClients / claimed,
                              color: againstColor,
                            },
                          ]
                        : []
                    }
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      columnGap: 1.5,
                      mt: 0.5,
                      typography: "caption",
                    }}
                  >
                    {legend(
                      gainFill(theme),
                      t("clients.claimedBy"),
                      claimedByClients,
                    )}
                    {legend(
                      againstColor,
                      t("clients.claimedAgainst"),
                      claimedAgainstClients,
                    )}
                  </Box>
                </FigureLink>
                <Box sx={{ pt: 0.5, borderTop: 1, borderColor: "divider" }}>
                  <FigureLink to={`${CLIENTS_PATH}#var`}>
                    <Row
                      label={t("clients.lossChance")}
                      value={
                        lossChance === undefined
                          ? NO_VALUE
                          : percent(lossChance)
                      }
                    />
                  </FigureLink>
                </Box>
              </>
            );
          }}
        </OverviewCard>

        {/* the settlements: how many cases settled, what they brought the
            clients, and what they changed against fighting the cases out */}
        <OverviewCard
          Icon={SettlementsIcon}
          label={t("settlements.label")}
          to={SETTLEMENTS_PATH}
          figures={figures}
        >
          {({
            settlements: {
              settledCases,
              settlements,
              opponents,
              receivedByClients,
              compared,
            },
          }) => (
            <>
              <FigureLink to={`${SETTLEMENTS_PATH}#overview`}>
                <Headline
                  value={String(settledCases)}
                  suffix={t("settlements.of", { count: total })}
                  caption={
                    settledCases > 0
                      ? `${t("settlements.settledCases", { count: settledCases })}: ${t("settlements.count", { count: settlements })} ${t("settlements.opponents", { count: opponents })}`
                      : t("settlements.settledCases", { count: settledCases })
                  }
                />
                <ShareBar
                  parts={[
                    {
                      share: total > 0 ? settledCases / total : 0,
                      color: gainFill(theme),
                    },
                  ]}
                />
              </FigureLink>
              {settledCases === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ py: 0.75, color: "text.secondary" }}
                >
                  {t("settlements.none")}
                </Typography>
              ) : (
                <div>
                  <FigureLink to={`${SETTLEMENTS_PATH}#overview`}>
                    <Row
                      label={t("settlements.received")}
                      value={currency.formatCompact(receivedByClients)}
                    />
                  </FigureLink>
                  <FigureLink to={`${SETTLEMENTS_PATH}#overview`}>
                    <Row
                      label={t("settlements.worstCase")}
                      value={
                        compared
                          ? currency.formatCompact(compared.worstCaseImproved)
                          : NO_VALUE
                      }
                      color={
                        compared &&
                        tones[signedTone(compared.worstCaseImproved)]
                      }
                    />
                  </FigureLink>
                  <FigureLink to={`${SETTLEMENTS_PATH}#savings`}>
                    <Row
                      label={t("settlements.costsAvoided")}
                      value={
                        compared
                          ? currency.formatCompact(compared.costsAvoided)
                          : NO_VALUE
                      }
                    />
                  </FigureLink>
                </div>
              )}
            </>
          )}
        </OverviewCard>
      </Box>
    </>
  );
};
